import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Container,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  StarRounded as StarRoundedIcon,
  StarBorderRounded as StarBorderRoundedIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message, FavoritePrompt } from '../services/chatService';
import { useAuth } from '../App';

// Assistant-message action buttons (thumbs up/down, retry) are temporarily hidden
// until there is a backend to record feedback. Flip to true to bring them back.
// Annotated as boolean so TS doesn't narrow the guarded JSX to unreachable code.
const SHOW_MESSAGE_ACTIONS: boolean = false;

// Get thinking message based on elapsed time
const getThinkingMessage = (seconds: number): string => {
  if (seconds < 15) {
    return "Thinking";
  } else if (seconds < 30) {
    return "Still thinking";
  } else if (seconds < 60) {
    return "Working on it";
  } else if (seconds < 90) {
    return "Processing (this may take a bit longer)";
  } else if (seconds < 120) {
    return "Still working on it";
  } else if (seconds < 180) {
    return "Processing (thanks for bearing with me)";
  } else if (seconds < 240) {
    return "Still processing (this is a complex request)";
  } else if (seconds < 270) {
    return "Almost there (thanks for your patience)";
  } else {
    return "Taking longer than expected (try a shorter date range if possible)";
  }
};

// Component to show live timer while thinking
const ThinkingTimer: React.FC<{ startTime: Date; statusMessage?: string }> = ({ startTime, statusMessage }) => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.round((new Date().getTime() - new Date(startTime).getTime()) / 1000);
      setSeconds(elapsed);
    }, 100); // Update every 100ms for smooth display

    return () => clearInterval(interval);
  }, [startTime]);

  const message = statusMessage || getThinkingMessage(seconds);

  return <span>{message} ({seconds}s)</span>;
};

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  assistantResponseRef?: React.RefObject<HTMLDivElement | null>;
  chatContainerRef?: React.RefObject<HTMLDivElement | null>;
  onRetryMessage?: (messageId: string) => void;
  currentStatus?: string | null;
  favoritePrompts?: FavoritePrompt[];
  onSaveFavoritePrompt?: (promptText: string) => Promise<void> | void;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  assistantResponseRef,
  chatContainerRef,
  onRetryMessage,
  currentStatus,
  favoritePrompts = [],
  onSaveFavoritePrompt,
}) => {
  const { user } = useAuth();

  // Helper function to get user initials
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user.username ? user.username.charAt(0).toUpperCase() : 'U';
  };
  const formatTimestamp = (timestamp: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(timestamp);
  };

  // Helper function to download CSV from table data
  const downloadTableAsCSV = (tableElement: HTMLTableElement) => {
    const rows = Array.from(tableElement.querySelectorAll('tr'));
    const csvContent = rows.map(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      return cells.map(cell => {
        // Clean the cell text and escape quotes
        const text = cell.textContent || '';
        return `"${text.replace(/"/g, '""')}"`;
      }).join(',');
    }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `table_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to download image
  const downloadImage = async (imageSrc: string) => {
    try {
      // Extract filename from URL or use default
      const filename = imageSrc.split('/').pop() || `chart_${new Date().toISOString().slice(0, 10)}.png`;

      // Fetch the image through our API (which handles S3 redirects)
      const response = await fetch(imageSrc, {
        credentials: 'include', // Include cookies for auth
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open in new tab
      window.open(imageSrc, '_blank');
    }
  };

  // Process message content to convert base64 strings to markdown images
  const processMessageContent = (content: string) => {
    // Regex to detect base64 strings that look like image data
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    
    // If the content looks like a base64 string (long string with base64 characters)
    if (content.length > 1000 && base64Regex.test(content.replace(/\s/g, ''))) {
      return `![Generated Chart](data:image/png;base64,${content})`;
    }
    
    // Also handle cases where content might contain base64 strings mixed with text
    return content.replace(
      /([A-Za-z0-9+/]{100,}={0,2})/g,
      (match) => {
        if (base64Regex.test(match)) {
          return `\n\n![Generated Chart](data:image/png;base64,${match})\n\n`;
        }
        return match;
      }
    );
  };

  // Create a ref for the latest user message to scroll to
  const latestUserMessageRef = useRef<HTMLDivElement>(null);

  // Scroll to position the latest user message at the top when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Find the last user message
      const userMessages = messages.filter(m => m.type === 'user');
      if (userMessages.length > 0) {
        // Use a slight delay to ensure DOM is updated
        setTimeout(() => {
          if (latestUserMessageRef.current) {
            latestUserMessageRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
    }
  }, [messages]);

  return (
    <Box
      ref={chatContainerRef}
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        backgroundColor: 'background.default', // Use theme background
        py: 3, // Vertical padding
        display: 'flex',
        justifyContent: 'center', // Center the content horizontally
        // Print styles
        '@media print': {
          overflow: 'visible',
          backgroundColor: 'white',
          py: 2, // Reduced padding for print
        },
      }}
    >
      <Box
        sx={{
          maxWidth: '960px',
          width: '100%',
          margin: '0 auto',
          px: { xs: 2, sm: 3 }, // Horizontal padding for content spacing
        }}
      >
        <Stack spacing={4}>
          {messages.map((message, index) => {
            // Check if this is the latest user message
            const userMessages = messages.filter(m => m.type === 'user');
            const isLatestUserMessage = message.type === 'user' && 
              userMessages.length > 0 && 
              message.id === userMessages[userMessages.length - 1].id;

            return (
              <Box 
                key={message.id} 
                ref={
                  message.type === 'assistant' && index === messages.length - 1 
                    ? assistantResponseRef 
                    : isLatestUserMessage 
                      ? latestUserMessageRef 
                      : undefined
                }
                data-message-type={message.type}
                sx={{
                  // Print styles for message boxes
                  '@media print': {
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                  },
                }}
              >
              {/* Claude-style message layout */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  // Add shading for user messages
                  backgroundColor: message.type === 'user' ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                  borderRadius: message.type === 'user' ? 1 : 0,
                  p: message.type === 'user' ? 2 : 1,
                  mx: message.type === 'user' ? -1 : 0,
                }}
              >
                {/* User avatar for user messages only */}
                {message.type === 'user' && (
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: '#2563eb',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    {getUserInitials()}
                  </Avatar>
                )}

                {/* Message Content */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {/* Message Body */}
                  <Box
                    sx={{
                      backgroundColor: 'transparent',
                      borderRadius: 0,
                    }}
                  >
                    {message.type === 'assistant' ? (
                      message.content.trim() ? (
                        <Box sx={{ 
                          '& > *': { mb: 1.5 }, 
                          '& > *:last-child': { mb: 0 },
                          // Add margin-top to elements that follow tables
                          '& table + *': { mt: '24px !important' },
                        }}>
                          <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (
                              <Typography 
                                variant="body1" 
                                component="div"
                                sx={{ 
                                  lineHeight: 1.6,
                                  color: 'text.primary'
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            h1: ({ children }) => (
                              <Typography variant="h5" component="h1" gutterBottom>
                                {children}
                              </Typography>
                            ),
                            h2: ({ children }) => (
                              <Typography variant="h6" component="h2" gutterBottom>
                                {children}
                              </Typography>
                            ),
                            h3: ({ children }) => (
                              <Typography variant="subtitle1" component="h3" gutterBottom>
                                {children}
                              </Typography>
                            ),
                            h4: ({ children }) => (
                              <Typography 
                                variant="h6" 
                                component="h4" 
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '1.125rem',
                                  color: 'text.primary',
                                  mt: 3,
                                  mb: 2.5,
                                  lineHeight: 1.3,
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            ul: ({ children }) => (
                              <Box 
                                component="ul" 
                                sx={{ 
                                  pl: 2.5,
                                  pr: 2,
                                  mb: 3,
                                  fontSize: '0.9375rem',
                                  lineHeight: 1.6,
                                  '& li': {
                                    mb: 0.5,
                                  }
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            ol: ({ children }) => (
                              <Box 
                                component="ol" 
                                sx={{ 
                                  pl: 2.5,
                                  pr: 2,
                                  mb: 3,
                                  fontSize: '0.9375rem',
                                  lineHeight: 1.6,
                                  '& li': {
                                    mb: 0.5,
                                  }
                                }}
                              >
                                {children}
                              </Box>
                            ),
                            li: ({ children }) => (
                              <Typography 
                                component="li" 
                                variant="body1"
                                sx={{
                                  fontSize: 'inherit',
                                  lineHeight: 'inherit',
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            code: ({ children, ...props }) => {
                              const isInline = !('className' in props && props.className?.includes('language-'));
                              return isInline ? (
                                <Typography
                                  component="code"
                                  sx={{
                                    bgcolor: 'background.default',
                                    border: 1,
                                    borderColor: 'divider',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                                    fontSize: '0.875em',
                                  }}
                                >
                                  {children}
                                </Typography>
                              ) : (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    bgcolor: 'background.default',
                                    border: 1,
                                    borderColor: 'divider',
                                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                                    fontSize: '0.875em',
                                    overflow: 'auto',
                                  }}
                                >
                                  <pre style={{ margin: 0 }}>{children}</pre>
                                </Paper>
                              );
                            },
                            table: ({ children }) => {
                              // Check if this message has CSV metadata for a full dataset download
                              const csvMetadata = message.csvMetadata;
                              const hasCsvMetadata = csvMetadata && csvMetadata.full_csv_download;

                              return (
                              <Box
                                className="table-wrapper"
                                sx={{
                                  mb: hasCsvMetadata ? 1 : 3,  // Reduce margin if we'll add download button below
                                  position: 'relative',
                                  borderRadius: { xs: 0.75, sm: 1 },
                                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                                  backgroundColor: 'background.paper',
                                  p: { xs: 1.5, sm: 2 },
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  overflow: 'hidden',
                                  // Show download button on hover of the entire table wrapper (for preview/quick download)
                                  '&:hover .hover-download-btn': {
                                    opacity: 1,
                                    visibility: 'visible',
                                    transform: 'translateY(0)',
                                  },
                                }}
                              >
                                {/* Visible Rows Download Button */}
                                {!hasCsvMetadata && (
                                  <Box
                                    component="button"
                                    className="hover-download-btn"
                                    onClick={(e) => {
                                      const tableElement = (e.target as HTMLElement)
                                        .closest('.table-wrapper')
                                        ?.querySelector('table') as HTMLTableElement;
                                      if (tableElement) {
                                        downloadTableAsCSV(tableElement);
                                      }
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      bottom: 12,
                                      right: 12,
                                      zIndex: 2,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 2,
                                      py: 0.75,
                                      backgroundColor: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      transition: 'all 0.15s ease',
                                      whiteSpace: 'nowrap',
                                      // Hidden by default, shown on hover
                                      opacity: 0,
                                      visibility: 'hidden',
                                      transform: 'translateY(8px)',
                                      '&:hover': {
                                        backgroundColor: '#2563eb',
                                      },
                                      // Always visible on touch devices (mobile)
                                      '@media (hover: none)': {
                                        opacity: 1,
                                        visibility: 'visible',
                                        transform: 'translateY(0)',
                                      },
                                      // Hide download buttons when printing
                                      '@media print': {
                                        display: 'none',
                                      },
                                    }}
                                    title="Download visible rows as CSV"
                                  >
                                    <GetAppIcon sx={{ fontSize: '1.125rem' }} />
                                    <span>Download</span>
                                  </Box>
                                )}

                                {/* Responsive Table Wrapper */}
                                <Box
                                  className="responsive-table-wrapper"
                                  sx={{
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    maxWidth: '100%',
                                    // Add shadow to indicate scrollable content
                                    position: 'relative',
                                    '&::after': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      bottom: 0,
                                      width: '30px',
                                      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
                                      pointerEvents: 'none',
                                      opacity: 0,
                                      transition: 'opacity 0.3s',
                                    },
                                    '&.scrollable::after': {
                                      opacity: 1,
                                    },
                                    // Smooth scrolling
                                    scrollBehavior: 'smooth',
                                    // Custom scrollbar styling
                                    '&::-webkit-scrollbar': {
                                      height: 10,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                      borderRadius: 4,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                      borderRadius: 4,
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    component="table"
                                    sx={{
                                      width: 'max-content',
                                      minWidth: '100%',
                                      borderCollapse: 'collapse',
                                      border: 1,
                                      borderColor: 'divider',
                                      borderRadius: 0,
                                      overflow: 'hidden',
                                      backgroundColor: 'background.paper',
                                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                      '& thead': {
                                        backgroundColor: 'action.hover',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                      },
                                      '& tbody tr:hover': {
                                        backgroundColor: 'action.hover',
                                      },
                                      // Let table columns size naturally based on content
                                      tableLayout: 'auto',
                                      // Print styles for tables
                                      '@media print': {
                                        minWidth: 'auto',
                                        tableLayout: 'auto',
                                        fontSize: '10px',
                                        '& thead': {
                                          position: 'static',
                                          backgroundColor: '#f5f5f5',
                                        },
                                        '& tbody tr:hover': {
                                          backgroundColor: 'transparent',
                                        },
                                        '& th, & td': {
                                          padding: '4px 6px',
                                          fontSize: '9px',
                                          lineHeight: 1.2,
                                        },
                                        pageBreakInside: 'auto',
                                      },
                                    }}
                                  >
                                    {children}
                                  </Box>
                                </Box>

                                {/* Full Dataset Download Button */}
                                {hasCsvMetadata && csvMetadata && csvMetadata.full_csv_row_count > 0 && (
                                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Box
                                      component="a"
                                      href={csvMetadata.full_csv_download}
                                      download
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 0.75,
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        borderRadius: '4px',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                          backgroundColor: '#2563eb',
                                        },
                                        '@media print': {
                                          display: 'none',
                                        },
                                      }}
                                    >
                                      <GetAppIcon sx={{ fontSize: '1.125rem' }} />
                                      <span>
                                        Download full dataset
                                        {csvMetadata.full_csv_row_count && ` (${csvMetadata.full_csv_row_count.toLocaleString()} rows)`}
                                      </span>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                              );
                            },
                            thead: ({ children }) => (
                              <thead>{children}</thead>
                            ),
                            tbody: ({ children }) => (
                              <tbody>{children}</tbody>
                            ),
                            tr: ({ children }) => (
                              <tr>{children}</tr>
                            ),
                            th: ({ children }) => (
                              <Typography
                                component="th"
                                sx={{
                                  p: 1,
                                  px: 1.5,
                                  fontWeight: 600,
                                  fontSize: '0.8125rem',
                                  color: 'text.primary',
                                  borderBottom: 1,
                                  borderColor: 'divider',
                                  textAlign: 'left',
                                  backgroundColor: 'action.hover',
                                  whiteSpace: 'nowrap',
                                  minWidth: 'fit-content',
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            td: ({ children }) => {
                              // Helper to format status text (e.g., "pending_approval" -> "Pending Approval")
                              const formatStatus = (text: string): string => {
                                if (typeof text !== 'string') return text;
                                // Check if it looks like a status (has underscores, lowercase)
                                if (text.includes('_') && text === text.toLowerCase()) {
                                  return text
                                    .split('_')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');
                                }
                                return text;
                              };

                              // Determine if this is a status cell
                              const cellText = typeof children === 'string' ? children : '';
                              const isStatus = cellText.includes('_') ||
                                               ['pending', 'approved', 'rejected', 'verified', 'active', 'inactive'].some(s =>
                                                 cellText.toLowerCase().includes(s));

                              const displayText = isStatus ? formatStatus(cellText) : children;

                              return (
                                <Typography
                                  component="td"
                                  sx={{
                                    p: 1,
                                    px: 1.5,
                                    fontSize: '0.8125rem',
                                    color: 'text.primary',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    verticalAlign: 'middle',
                                    whiteSpace: 'nowrap',
                                    minWidth: 'fit-content',
                                    maxWidth: '300px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '&:hover': {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                    },
                                  }}
                                  title={typeof children === 'string' ? children : undefined}
                                >
                                  {displayText}
                                </Typography>
                              );
                            },
                            img: ({ src, alt, ...props }) => {
                              
                              // Handle different URL patterns
                              let imageSrc = src;
                              
                              if (src) {
                                // Clean up any extra closing parentheses at the end
                                let cleanSrc = src.replace(/\)+$/, '');
                                
                                if (cleanSrc.startsWith('/api/plots/')) {
                                  // Direct relative URL - use as-is (nginx will proxy)
                                  imageSrc = cleanSrc;
                                } else if (cleanSrc.includes('/api/plots/') && !cleanSrc.startsWith('http')) {
                                  // Malformed URL with extra path - extract just the plots part
                                  const plotsMatch = cleanSrc.match(/\/api\/plots\/[^/\s)]+$/);
                                  if (plotsMatch) {
                                    imageSrc = plotsMatch[0];
                                  }
                                }
                              }
                              
                              
                              return (
                              <Box 
                                sx={{ 
                                  mb: 3, 
                                  display: 'flex',
                                  justifyContent: 'center',
                                  width: '100%',
                                  px: { xs: 1, sm: 0 }, // Add horizontal padding on mobile
                                }}
                              >
                                {/* Chart Wrapper with Hover-Only Download Button */}
                                <Box
                                  className="chart-wrapper"
                                  sx={{
                                    position: 'relative',
                                    maxWidth: '100%',
                                    width: '100%',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                                    backgroundColor: 'background.paper',
                                    p: { xs: 1.5, sm: 2 },
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    // Show download button on hover of the entire chart wrapper
                                    '&:hover .download-btn': {
                                      opacity: 1,
                                      visibility: 'visible',
                                      transform: 'translateY(0)',
                                    },
                                  }}
                                >
                                  {/* Hover-Only Download Button - Bottom Right */}
                                  <Box
                                    component="button"
                                    className="download-btn"
                                    onClick={() => imageSrc && downloadImage(imageSrc)}
                                    sx={{
                                      position: 'absolute',
                                      bottom: { xs: 8, sm: 12 },
                                      right: { xs: 8, sm: 12 },
                                      zIndex: 2,
                                      backgroundColor: '#f5f7fa',
                                      border: 'none',
                                      borderRadius: { xs: '6px', sm: '8px' },
                                      p: { xs: '4px 6px', sm: '6px 10px' },
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                      minWidth: { xs: '28px', sm: '32px' },
                                      minHeight: { xs: '28px', sm: '32px' },
                                      // Hidden by default
                                      opacity: 0,
                                      visibility: 'hidden',
                                      transform: 'translateY(8px)',
                                      backdropFilter: 'blur(8px)',
                                      '&:hover': {
                                        backgroundColor: '#e0f7fa',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                      },
                                      '&:active': {
                                        transform: 'translateY(0)',
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                                      },
                                      // Always visible on touch devices (mobile)
                                      '@media (hover: none)': {
                                        opacity: 0.8,
                                        visibility: 'visible',
                                        transform: 'translateY(0)',
                                      },
                                      // Hide download buttons when printing
                                      '@media print': {
                                        display: 'none',
                                      },
                                    }}
                                    title="Download chart"
                                  >
                                    <DownloadIcon 
                                      sx={{ 
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        color: '#374151',
                                      }} 
                                    />
                                  </Box>

                                  <img
                                    src={imageSrc}
                                    alt={alt || 'Chart'}
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      borderRadius: '4px',
                                      display: 'block',
                                    }}
                                    onError={(e) => {
                                      console.error('Image failed to load:', imageSrc);
                                      console.error('Error event:', e);
                                    }}
                                    onLoad={() => {
                                    }}
                                    {...props}
                                  />
                                </Box>
                              </Box>
                            );
                            },
                            a: ({ href, children, ...props }) => {
                              // Check if this is a CSV/data file download link
                              const isDownloadLink = href && (
                                href.includes('/api/data/') ||
                                href.includes('/api/plots/') ||
                                href.endsWith('.csv') ||
                                href.endsWith('.png')
                              );

                              return (
                                <a
                                  href={href}
                                  target={isDownloadLink ? undefined : "_blank"}
                                  rel={isDownloadLink ? undefined : "noopener noreferrer"}
                                  download={isDownloadLink ? true : undefined}
                                  style={{ color: '#1976d2', textDecoration: 'none' }}
                                  {...props}
                                >
                                  {children}
                                </a>
                              );
                            },
                          }}
                        >
                          {(() => {
                            const processed = processMessageContent(message.content);
                            // Debug logging for plot URLs
                            if (processed.includes('/api/plots/')) {
                            }
                            return processed;
                          })()}
                        </ReactMarkdown>
                      </Box>
                      ) : (
                        // Show progressive thinking messages for empty assistant messages
                        (() => {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="text.secondary" component="div">
                                {message.thinkingStartTime ? (
                                  currentStatus ? (
                                    <ThinkingTimer startTime={message.thinkingStartTime} statusMessage={currentStatus} />
                                  ) : (
                                    <ThinkingTimer startTime={message.thinkingStartTime} />
                                  )
                                ) : null}
                              </Typography>
                            </Box>
                          );
                        })()
                      )
                    ) : (
                      <Typography 
                        variant="body1"
                        sx={{ 
                          lineHeight: 1.6,
                          color: 'text.primary'
                        }}
                      >
                        {message.content}
                      </Typography>
                    )}
                  </Box>

                  {/* Save a question as a favorite prompt. Shown on hover so it
                      stays out of the way, and filled once the prompt is saved. */}
                  {message.type === 'user' && onSaveFavoritePrompt && (() => {
                    const isSaved = favoritePrompts.some(p => p.prompt_text === message.content);

                    return (
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end',
                        '@media print': { display: 'none' } }}>
                        <IconButton
                          size="small"
                          onClick={() => !isSaved && onSaveFavoritePrompt(message.content)}
                          disabled={isSaved}
                          aria-label={isSaved ? 'Saved to your favorite prompts' : 'Save this prompt'}
                          title={isSaved ? 'Saved to your favorite prompts' : 'Save this prompt'}
                          sx={{
                            p: 0.5,
                            color: isSaved ? '#f59e0b' : 'text.secondary',
                            opacity: isSaved ? 1 : 0,
                            transition: 'opacity 0.15s ease',
                            '[data-message-type="user"]:hover &': { opacity: 1 },
                            '&:focus-visible': { opacity: 1 },
                            '&.Mui-disabled': { color: '#f59e0b' },
                            '&:hover': { color: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.08)' },
                          }}
                        >
                          {isSaved ? (
                            <StarRoundedIcon sx={{ fontSize: '1.15rem' }} />
                          ) : (
                            <StarBorderRoundedIcon sx={{ fontSize: '1.15rem' }} />
                          )}
                        </IconButton>
                      </Box>
                    );
                  })()}


                  {/* Show thinking duration after response */}
                  {message.type === 'assistant' && message.content.trim() && message.thinkingDuration && (
                    <Box sx={{ mt: 1, mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          fontStyle: 'italic',
                        }}
                      >
                        ⏱ Generated in {message.thinkingDuration}s
                      </Typography>
                    </Box>
                  )}

                  {/* Action buttons and disclaimer for assistant messages */}
                  {message.type === 'assistant' && message.content.trim() && (
                    <Box sx={{ 
                      mt: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'flex-end',
                      // Hide action buttons when printing
                      '@media print': {
                        display: 'none',
                      },
                    }}>
                      {/* Action buttons */}
                      {SHOW_MESSAGE_ACTIONS && <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => {/* TODO: Implement thumbs up */}}
                          sx={{
                            color: 'text.secondary',
                            p: 0.5,
                            '&:hover': { 
                              color: 'success.main', 
                              backgroundColor: 'success.light',
                            },
                          }}
                        >
                          <ThumbUpIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                        
                        <IconButton
                          size="small"
                          onClick={() => {/* TODO: Implement thumbs down */}}
                          sx={{
                            color: 'text.secondary',
                            p: 0.5,
                            '&:hover': { 
                              color: 'error.main', 
                              backgroundColor: 'error.light',
                            },
                          }}
                        >
                          <ThumbDownIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                        
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (onRetryMessage) {
                              // Find the previous user message
                              const currentIndex = messages.findIndex(m => m.id === message.id);
                              if (currentIndex > 0) {
                                const previousMessage = messages[currentIndex - 1];
                                if (previousMessage.type === 'user') {
                                  onRetryMessage(previousMessage.id);
                                }
                              }
                            }
                          }}
                          disabled={!onRetryMessage || isLoading}
                          sx={{
                            color: 'text.secondary',
                            p: 0.5,
                            '&:hover': { 
                              color: 'primary.main', 
                              backgroundColor: 'primary.light',
                            },
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Stack>}

                      {/* Disclaimer text */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ 
                          fontSize: '0.675rem', // 10% smaller than 0.75rem
                          fontStyle: 'italic',
                        }}
                      >
                        Otti can make mistakes. Please double-check responses.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            );
          })}

          {/* Loading indicator removed - now handled by empty assistant message */}
        </Stack>
      </Box>
    </Box>
  );
};

export default MessageList;